import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import clientService from '../../../services/clientService';
import { ArrowLeft, Users, Briefcase, DollarSign, TrendingUp, Mail, Phone, MapPin, Plus } from 'lucide-react';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      const data = await clientService.getClient(id);
      setClient(data);
    } catch (e) {
      console.error(e);
      alert('Unable to load client');
      navigate('/admin/clients');
    } finally {
      setLoading(false);
    }
  };

  const markCompleted = async (projectId) => {
    try {
      setUpdating(true);
      await clientService.updateProjectProgress(projectId, 100);
      // Update local state
      setClient(prev => ({
        ...prev,
        projects: prev.projects?.map(p => p.id === projectId ? { ...p, progress: 100, status: 'completed' } : p)
      }));
    } catch (e) {
      alert('Failed to mark project completed');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!client) return null;

  const stats = {
    total_projects: client.total_projects || client.projects?.length || 0,
    active_projects: client.active_projects || (client.projects?.filter(p=>p.status==='in_progress').length || 0),
    total_paid: client.total_paid || 0,
    pending_payments: client.pending_payments || 0,
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button onClick={()=>navigate('/admin/clients')} className="flex items-center gap-2 text-gray-600 hover:text-primary mb-4"><ArrowLeft className="w-5 h-5"/>Back to Clients</button>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold">
              {client.company_name?.charAt(0) || 'C'}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-primary">{client.company_name}</h1>
              <p className="text-gray-600">{client.contact_person}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/admin/projects/new?client_id=${client.id}`} className="bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"><Plus className="w-4 h-4"/> Add Project</Link>
            <Link to={`/admin/payments/new?client_id=${client.id}`} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"><Plus className="w-4 h-4"/> Add Payment</Link>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Total Projects</p><p className="text-3xl font-bold text-primary mt-2">{stats.total_projects}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Active Projects</p><p className="text-3xl font-bold text-green-600 mt-2">{stats.active_projects}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Total Paid</p><p className="text-3xl font-bold text-purple-600 mt-2">฿{Number(stats.total_paid).toLocaleString()}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Pending</p><p className="text-3xl font-bold text-orange-600 mt-2">฿{Number(stats.pending_payments).toLocaleString()}</p></div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md">
        <div className="border-b px-4 sm:px-6 lg:px-8">
          <div className="flex gap-4">
            {['overview','projects','payments','notes'].map(tab => (
              <button key={tab} onClick={()=>setActiveTab(tab)} className={`py-3 px-4 border-b-2 -mb-px font-semibold ${activeTab===tab? 'border-primary text-primary':'border-transparent text-gray-500 hover:text-primary'}`}>{tab.charAt(0).toUpperCase()+tab.slice(1)}</button>
            ))}
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Contact</h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2"><Mail className="w-4 h-4"/>{client.email}</div>
                    <div className="flex items-center gap-2"><Phone className="w-4 h-4"/>{client.phone}</div>
                    {client.address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-1"/><span>{client.address}</span></div>}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Tax IDs</h3>
                  <p className="text-sm text-gray-700">GST: {client.gst_number || '—'}</p>
                  <p className="text-sm text-gray-700">PAN: {client.pan_number || '—'}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Project</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Budget / Paid</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {client.projects?.length ? client.projects.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-semibold">{p.project_name || p.title}</td>
                      <td className="px-6 py-4 capitalize">{(p.project_type || '').replace('_',' ') || '—'}</td>
                      <td className="px-6 py-4">฿{Number(p.budget||0).toLocaleString()} / <span className="text-green-600">฿{Number(p.paid_amount||0).toLocaleString()}</span></td>
                      <td className="px-6 py-4">
                        <div className="w-32">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="h-2 bg-primary rounded-full" style={{ width: `${p.progress||0}%` }} />
                          </div>
                          <div className="text-xs text-gray-600 mt-1">{p.progress || 0}%</div>
                        </div>
                      </td>
                      <td className="px-6 py-4"><span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">{(p.status||'').replace('_',' ')}</span></td>
                      <td className="px-6 py-4 text-right">
                        {p.progress < 100 && (
                          <button disabled={updating} onClick={() => markCompleted(p.id)} className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50">Mark Completed</button>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">No projects yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Project</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {client.payments?.length ? client.payments.map(pm => (
                    <tr key={pm.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-semibold">{pm.invoice_number}</td>
                      <td className="px-6 py-4">{pm.project?.project_name || '—'}</td>
                      <td className="px-6 py-4">฿{Number(pm.amount).toLocaleString()}</td>
                      <td className="px-6 py-4">{pm.payment_date}</td>
                      <td className="px-6 py-4">{pm.status}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">No payments yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'notes' && (
            <div>
              <p className="text-gray-700 whitespace-pre-wrap">{client.notes || 'No notes yet'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
